import { Card, ListGroup } from 'react-bootstrap';
import type { CommitteeMember, UpcomingEvent } from '../types/api.types';
import type { MemberLookupFn } from '../utils/committee';
import { isEventFromToday } from '../utils/events';

interface CommitteeMembersProps {
  committeeMembers: CommitteeMember[];
  memberLookup: MemberLookupFn;
  upcomingEvents: UpcomingEvent[];
}

interface CommitteeMemberDisplay {
  id: string;
  role: string;
  name: string;
}

interface UpcomingEventItemProps {
  upcomingEvent: UpcomingEvent;
}

function UpcomingEventItem({ upcomingEvent }: UpcomingEventItemProps) {
  const sideVenueImage = upcomingEvent.image ? (
    <img className="side-venue-image" src={upcomingEvent.image} alt="" style={{ float: 'right' }} />
  ) : null;

  const bottomVenueImage = upcomingEvent.image ? (
    <img className="bottom-venue-image" src={upcomingEvent.image} alt="" style={{ margin: 'auto' }} />
  ) : null;

  const venue = upcomingEvent.venue ? (
    <div style={{ fontWeight: 'bold', fontSize: 'large' }}>
      <span>@ </span>
      <a href={upcomingEvent.url}>{upcomingEvent.venue}</a>
    </div>
  ) : null;

  const organiser1 = upcomingEvent.organiser1 ? (
    <img src={`/assets/${upcomingEvent.organiser1}.jpg`} height="45px" alt="" />
  ) : null;

  const organiser2 = upcomingEvent.organiser2 ? (
    <img src={`/assets/${upcomingEvent.organiser2}.jpg`} height="45px" alt="" />
  ) : null;

  const details = upcomingEvent.details ? (
    <span style={{ fontStyle: 'italic' }}> "{upcomingEvent.details}"</span>
  ) : null;

  return (
    <ListGroup.Item>
      {sideVenueImage}
      <div>
        <span style={{ fontWeight: 'bold' }}>{upcomingEvent.date}: </span>
        <span style={{ fontStyle: 'italic' }}>{upcomingEvent.eventType}</span>
      </div>
      {venue}
      <div>
        <span>{organiser1}</span>
        <span> </span>
        <span>{organiser2}</span>
        {details}
      </div>
      {bottomVenueImage}
    </ListGroup.Item>
  );
}

// Build committee members list from latest committee data using provided lookup function
import { buildCommitteeMembers } from '../utils/committee';

// Component for rendering a single committee member card
const CommitteeMemberCard = ({ member }: { member: CommitteeMemberDisplay }) => (
  <div
    key={`${member.id}-${member.role}`}
    style={{
      textAlign: 'center',
      maxWidth: '120px',
    }}
  >
    <img
      src={`/assets/${member.id}.jpg`}
      alt={member.name}
      style={{
        width: '100px',
        height: '100px',
        objectFit: 'cover',
        borderRadius: '8px',
        marginBottom: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    />
    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>{member.name}</div>
    <div style={{ fontSize: '12px', color: '#666' }}>{member.role}</div>
  </div>
);

export const CommitteeMembers = ({ committeeMembers, memberLookup, upcomingEvents }: CommitteeMembersProps) => {
  // Get the last set of values from the list (most recent season's committee)
  const latestCommittee = committeeMembers.length > 0 ? committeeMembers[committeeMembers.length - 1] : null;

  if (!latestCommittee) {
    return null;
  }

  // Build list of committee members with their roles
  const members = buildCommitteeMembers(latestCommittee, memberLookup);

  // Filter and render upcoming events
  const eventsFromToday = upcomingEvents.filter(isEventFromToday);
  const nextEventListItems = eventsFromToday.map((upcomingEvent) => (
    <UpcomingEventItem upcomingEvent={upcomingEvent} key={upcomingEvent.date} />
  ));

  return (
    <Card style={{ marginBottom: '20px' }}>
      <Card.Header>Committee</Card.Header>
      <Card.Body>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          {members.map((member) => (
            <CommitteeMemberCard key={`${member.id}-${member.role}`} member={member} />
          ))}
        </div>

        {eventsFromToday.length > 0 && (
          <ListGroup variant="flush" className="upcoming-events">
            {nextEventListItems}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
};

export default CommitteeMembers;
